import { supabase } from '@/config/supabase';
import { getAIEndpoint } from '@/config/apiEndpoints';
import { parseJSONArray } from '@/utils/jsonParser';
import type { ExamDifficulty, ExamMCQQuestion } from '@/types/exam';

interface RoadmapContent {
  title: string;
  description?: string;
  skills?: string[];
  topics?: string[];
  level?: string;
}

/**
 * AI-powered exam question generator
 * Generates MCQ questions dynamically based on roadmap content and learning goals
 */
export const aiExamQuestionGenerator = {
  /**
   * Generate exam questions using AI based on roadmap content
   */
  async generateExamQuestions(
    roadmapId: string,
    difficulty: ExamDifficulty,
    count: number = 30
  ): Promise<Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[]> {
    try {
      // Get roadmap content
      const roadmapContent = await this.getRoadmapContent(roadmapId);
      
      // Call AI to generate questions
      const questions = await this.callAIQuestionGenerator(
        roadmapContent,
        difficulty,
        count
      );

      // Questions are generated on-the-fly and not cached
      // Only exam attempt scores are stored in the database
      return questions;
    } catch (error) {
      console.error('Error generating exam questions:', error);
      throw error;
    }
  },

  /**
   * Get roadmap content for question generation context
   */
  async getRoadmapContent(roadmapId: string): Promise<RoadmapContent> {
    try {
      // Fetch roadmap - use actual columns: title, goal, intensity
      const { data: roadmap, error: roadmapError } = await supabase
        .from('roadmaps')
        .select('id, title, goal, intensity')
        .eq('id', roadmapId)
        .maybeSingle();

      if (roadmapError) {
        console.warn('Error fetching roadmap:', roadmapError);
      }

      if (!roadmap) {
        console.warn(`Roadmap not found: ${roadmapId} - using basic context`);
        // Return basic context if roadmap not found
        return {
          title: roadmapId,
          description: `Generate exam questions for the ${roadmapId} learning path`,
          skills: [roadmapId],
          level: 'beginner',
        };
      }

      // Get articles (lessons) for this roadmap to extract comprehensive context
      // Only select columns that actually exist in the schema
      const { data: articlesData, error: articlesError } = await supabase
        .from('articles')
        .select('title, topic, technical_concepts, deep_dive, subtitle')
        .eq('roadmap_id', roadmapId)
        .limit(20); // Get more articles for better context
      
      if (articlesError) {
        console.warn('Error fetching articles:', articlesError);
      }

      const articles = articlesData || [];

      // Extract comprehensive topics and learning points
      const topics = new Set<string>();
      const descriptions: string[] = [];
      
      articles.forEach((article: any) => {
        if (article.topic) topics.add(article.topic);
        if (article.technical_concepts && Array.isArray(article.technical_concepts)) {
          article.technical_concepts.forEach((concept: string) => {
            if (concept && typeof concept === 'string') {
              topics.add(concept);
            }
          });
        }
        // Add article titles as key learning areas
        if (article.title) {
          topics.add(article.title);
          const desc = [article.title, article.subtitle, article.deep_dive]
            .filter(Boolean)
            .join(': ');
          if (desc) descriptions.push(desc);
        }
      });

      // Combine goal with article descriptions for richer context
      const enrichedDescription = [
        roadmap.goal || 'Master the subject matter',
        ...(descriptions.length > 0 
          ? descriptions.slice(0, 5) 
          : [`This is a course about ${roadmap.title}`]
        ) // Add top 5 article descriptions or fallback
      ].join('\n\n');

      return {
        title: roadmap.title || '',
        description: enrichedDescription, // Use enriched description with actual course content
        skills: Array.from(topics), // Convert Set to array with both topics and titles
        level: this.mapIntensityToDifficulty(roadmap.intensity),
      };
    } catch (error) {
      console.error('Error fetching roadmap content:', error);
      throw error;
    }
  },

  /**
   * Map roadmap intensity to exam difficulty
   */
  mapIntensityToDifficulty(intensity?: string): string {
    const intensityMap: Record<string, string> = {
      Beginner: 'beginner',
      Intermediate: 'intermediate',
      Advanced: 'expert',
      Expert: 'expert',
    };
    return intensityMap[intensity || 'Beginner'] || 'beginner';
  },

  /**
   * Call AI API to generate exam questions via backend
   */
  async callAIQuestionGenerator(
    roadmapContent: RoadmapContent,
    difficulty: ExamDifficulty,
    count: number
  ): Promise<Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[]> {
    try {
      const prompt = this.buildGenerationPrompt(roadmapContent, difficulty, count);

      console.log('🚀 Calling AI endpoint:', getAIEndpoint());
      console.log('📝 Prompt length:', prompt.length);
      console.log('🎯 Difficulty:', difficulty, '| Count:', count);

      // Call backend AI generate endpoint which uses Gemini
      const response = await fetch(getAIEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          type: 'exam-questions',
          difficulty,
          count,
          jsonMode: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Backend API error:', response.status, response.statusText);
        console.error('📋 Error details:', errorData);
        console.warn('⚠️ Falling back to hardcoded questions...');
        return this.generateFallbackQuestions(count);
      }

      const data = (await response.json()) as any;
      
      console.log('✅ Backend AI response received, length:', JSON.stringify(data).length);
      console.log('📊 Response structure:', { 
        hasData: !!data.data, 
        hasContent: !!data.content, 
        hasText: !!data.text,
        keys: Object.keys(data).slice(0, 5)
      });
      
      // Backend returns text content, not pre-formatted questions
      let responseText = '';
      
      // Try multiple extraction strategies in order of likelihood
      if (data.data && typeof data.data === 'string') {
        // Most likely: { data: "JSON string" }
        responseText = data.data;
      } else if (data.data && typeof data.data === 'object') {
        // Nested object structure: { data: { questions: [...] } }
        responseText = JSON.stringify(data.data);
      } else if (typeof data === 'string') {
        responseText = data;
      } else if (data.content) {
        responseText = data.content;
      } else if (data.text) {
        responseText = data.text;
      } else if (data.message) {
        responseText = data.message;
      } else if (data.result) {
        responseText = data.result;
      } else if (data.response) {
        responseText = data.response;
      } else if (typeof data === 'object' && Object.keys(data).length > 0) {
        // Try first non-empty string value
        const values = Object.values(data);
        const foundString = values.find((v: any) => {
          const str = String(v);
          return str.length > 10 && (str.includes('[') || str.includes('{'));
        });
        responseText = foundString ? String(foundString) : '';
      }
      
      if (!responseText || responseText.length < 10) {
        console.warn('⚠️ Could not extract valid text from backend response');
        console.warn('📋 Raw response:', JSON.stringify(data).substring(0, 200));
        return this.generateFallbackQuestions(count);
      }

      console.log('📝 Extracted response text length:', responseText.length);
      console.log('📝 First 200 chars:', responseText.substring(0, 200));

      // Parse the AI response text to extract questions
      const questions = this.parseAIResponse(responseText, difficulty);
      
      if (!questions || questions.length === 0) {
        console.warn('⚠️ No questions parsed from backend response - using fallback questions');
        return this.generateFallbackQuestions(count);
      }

      console.log('✨ Successfully generated', questions.length, 'questions from AI');
      return questions.slice(0, count);
    } catch (error) {
      console.error('❌ Error calling AI question generator:', error);
      console.warn('⚠️ Using fallback questions instead');
      return this.generateFallbackQuestions(count);
    }
  },

  /**
   * Generate fallback questions when API is unavailable
   */
  generateFallbackQuestions(count: number): Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[] {
    const fallbackQuestions: Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[] = [
      {
        question: 'What is the primary objective of this course?',
        options: ['To learn fundamentals', 'To build real projects', 'To master advanced concepts', 'To prepare for industry roles'],
        correctAnswer: 0,
        difficulty: 'beginner',
      },
      {
        question: 'Which approach is most effective for learning?',
        options: ['Theory only', 'Theory combined with practice', 'Practice only', 'Memorization only'],
        correctAnswer: 1,
        difficulty: 'beginner',
      },
      {
        question: 'How should you measure your progress?',
        options: ['By watching videos', 'By completing projects and assessments', 'By reading documentation', 'By memorizing concepts'],
        correctAnswer: 1,
        difficulty: 'beginner',
      },
      {
        question: 'What is essential for mastery?',
        options: ['Speed of learning', 'Consistent practice and repetition', 'Using only one resource', 'Avoiding mistakes'],
        correctAnswer: 1,
        difficulty: 'beginner',
      },
    ];

    // Repeat fallback questions if needed
    const questions: Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[] = [];
    for (let i = 0; i < count; i++) {
      questions.push({...fallbackQuestions[i % fallbackQuestions.length]});
    }
    return questions;
  },

  /**
   * Build prompt for AI to generate questions
   */
  buildGenerationPrompt(
    roadmapContent: RoadmapContent,
    difficulty: ExamDifficulty,
    count: number
  ): string {
    // Calculate question distribution
    const easyCount = Math.ceil(count * 0.3); // 30% easy
    const mediumCount = Math.ceil(count * 0.4); // 40% medium
    const hardCount = count - easyCount - mediumCount; // 30% hard

    return `Create ${count} unique analytical multiple-choice exam questions for "${roadmapContent.title}".

DISTRIBUTION:
- ${easyCount} EASY questions (foundation & definitions)
- ${mediumCount} MEDIUM questions (application & analysis)
- ${hardCount} HARD questions (edge cases & synthesis)

REQUIREMENTS:
1. Questions must test the core concepts of this roadmap, not generic knowledge
2. Each question has exactly 4 distinct options with ONE correct answer
3. Randomize option order (correct answer can be at any position 0-3)
4. Include clear explanations for each correct answer
5. Make questions progressively analytical - test understanding, not memorization
6. Ensure COMPLETE UNIQUENESS - no duplicate or similar questions

TOPICS: ${roadmapContent.skills?.join(', ') || roadmapContent.title}

OUTPUT: Valid JSON array with this structure (nothing else):
[
  {
    "question": "Analytical question about the topic?",
    "options": ["Option A", "Option B", "Option C", "Option D"],
    "correct_answer": 0,
    "explanation": "Why this answer is correct"
  }
]

Generate exactly ${count} unique analytical questions:`;
  },

  /**
   * Parse AI response and convert to ExamMCQQuestion format
   */
  parseAIResponse(
    content: string,
    difficulty: ExamDifficulty
  ): Omit<ExamMCQQuestion, 'id' | 'roadmap_id'>[] {
    try {
      // Use robust JSON parser
      const parseResult = parseJSONArray<any>(content, 'Exam Questions');

      if (!parseResult.success || !parseResult.data) {
        console.warn(`⚠️  Failed to parse questions: ${parseResult.error}`);
        return [];
      }

      const aiQuestions = parseResult.data;

      // Ensure uniqueness first
      const uniqueQuestions = this.ensureUniqueQuestions(aiQuestions);

      return uniqueQuestions
        .filter(
          (q: any) =>
            q &&
            q.question &&
            q.options &&
            Array.isArray(q.options) &&
            q.options.length === 4
        )
        .map((q: any) => {
          const correctIndex = typeof q.correct_answer === 'number' ? q.correct_answer : 0;
          // Shuffle options and track new correct index
          const { shuffledOptions, newCorrectIndex } = this.shuffleOptionsAndTrackCorrect(
            q.options.map((opt: any) => String(opt).trim()),
            correctIndex
          );

          return {
            difficulty,
            question: String(q.question).trim(),
            options: shuffledOptions,
            correctAnswer: newCorrectIndex,
            explanation: q.explanation
              ? String(q.explanation).trim()
              : 'Check your understanding of the roadmap content.',
            tags: [difficulty, 'auto-generated', 'roadmap-specific'],
          };
        });
    } catch (error) {
      console.error('❌ Error parsing AI response:', error);
      console.log('📋 Content length:', content.length);
      return [];
    }
  },

  /**
   * Shuffle answer options while tracking the new correct answer index
   */
  shuffleOptionsAndTrackCorrect(
    options: string[],
    correctIndex: number
  ): { shuffledOptions: string[]; newCorrectIndex: number } {
    if (!Array.isArray(options) || options.length !== 4) {
      return { shuffledOptions: options, newCorrectIndex: correctIndex };
    }

    // Create array of [option, originalIndex] pairs
    const indexed = options.map((opt, idx) => ({ opt, idx }));

    // Fisher-Yates shuffle
    for (let i = indexed.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
    }

    // Extract shuffled options and find new correct index
    const shuffledOptions = indexed.map((item) => item.opt);
    const newCorrectIndex = indexed.findIndex((item) => item.idx === correctIndex);

    return {
      shuffledOptions,
      newCorrectIndex: newCorrectIndex >= 0 ? newCorrectIndex : correctIndex,
    };
  },

  /**
   * Ensure questions are unique by question text
   */
  ensureUniqueQuestions(
    questions: Array<{ question: string; [key: string]: any }>
  ): Array<{ question: string; [key: string]: any }> {
    const seen = new Set<string>();
    const unique: Array<{ question: string; [key: string]: any }> = [];

    for (const q of questions) {
      // Normalize question text for comparison
      const normalized = q.question.toLowerCase().trim();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        unique.push(q);
      }
    }

    return unique;
  },

};
